import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Formik, Form, Field, FieldArray } from 'formik';
import { toast } from 'sonner';
import { Button } from '@/src/shadComponents/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/shadComponents/ui/label';
import { Textarea } from '@/src/shadComponents/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import useQrAutomation, {
    QrAutomationSettings as QrAutomationSettingsType,
} from './hooks/useQrAutomation';
import useGetGalleries from '../../GalleryConfiguration/components/hooks/useGetGalleries';
import SmallLoader from '../../Common/components/SmallLoader';

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'phoneNumber', label: 'Phone Number' },
    { value: 'boolean', label: 'Checkbox' },
];

interface FormValues {
    interface: {
        title: string;
        submitButtonText: string;
        confirmationText: string;
        galleryId: number;
    };
    dataFields: Array<{
        type: 'boolean' | 'text' | 'email' | 'phoneNumber';
        name: string;
        placeholder: string;
        galleryId: number;
    }>;
}

export default function QrAutomationSettings() {
    const { activationId } = useParams<{ activationId: string }>();
    const [settings, setSettings] = useState<QrAutomationSettingsType | null>(
        null,
    );
    const [galleries, setGalleries] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const {
        getAutomationSettings,
        updateAutomationSettings,
        isLoading: isSubmitting,
    } = useQrAutomation();
    const { getGalleriesForActivation } = useGetGalleries();

    useEffect(() => {
        const fetchData = async () => {
            if (!activationId) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const [settingsData, galleriesData] = await Promise.all([
                    getAutomationSettings(+activationId),
                    getGalleriesForActivation(+activationId),
                ]);

                setSettings(settingsData);
                setGalleries(galleriesData);
            } catch (error) {
                console.error('Error fetching QR automation data:', error);
                toast.error('Failed to load QR automation settings');
                // Set empty state on error to prevent infinite loading
                setSettings(null);
                setGalleries([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
        // Only depend on activationId to prevent infinite loops
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activationId]);

    const handleSubmit = async (values: FormValues) => {
        if (!activationId) return;

        // Ensure we have a valid gallery ID
        if (!values.interface.galleryId) {
            toast.error('Please select a gallery before saving');
            return;
        }

        try {
            const updatedSettings = await updateAutomationSettings(
                +activationId,
                {
                    interface: values.interface,
                    dataFields: values.dataFields.map((field, index) => ({
                        ...field,
                        orderIndex: index,
                    })),
                },
            );

            setSettings(updatedSettings);
            toast.success('QR automation settings saved successfully');
        } catch (error) {
            console.error('Error saving QR automation settings:', error);
            toast.error('Failed to save QR automation settings');
        }
    };

    const moveFieldUp = (
        index: number,
        setFieldValue: any,
        values: FormValues,
    ) => {
        if (index === 0) return;
        const items = Array.from(values.dataFields);
        const [item] = items.splice(index, 1);
        items.splice(index - 1, 0, item);
        setFieldValue('dataFields', items);
    };

    const moveFieldDown = (
        index: number,
        setFieldValue: any,
        values: FormValues,
    ) => {
        if (index === values.dataFields.length - 1) return;
        const items = Array.from(values.dataFields);
        const [item] = items.splice(index, 1);
        items.splice(index + 1, 0, item);
        setFieldValue('dataFields', items);
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <SmallLoader />
            </div>
        );
    }

    if (galleries.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
                <div className="text-lg text-muted-foreground">
                    Unable to load settings.
                </div>
                <div className="text-sm text-muted-foreground">
                    Please try refreshing the page or contact support if the
                    problem persists.
                </div>
            </div>
        );
    }

    const defaultGalleryId = galleries[0]?.id || 0;

    const initialValues: FormValues = {
        interface: {
            title: settings?.interface?.title || '',
            submitButtonText: settings?.interface?.submitButtonText || 'Next',
            confirmationText:
                settings?.interface?.confirmationText ||
                'Great! Look at the photo booth for next steps!',
            galleryId: settings?.interface?.galleryId || defaultGalleryId,
        },
        dataFields:
            settings?.dataFields?.map((field) => ({
                type: field.type,
                name: field.name,
                placeholder: field.placeholder || '',
                galleryId: field.galleryId,
            })) || [],
    };

    return (
        <div className="max-w-4xl mx-auto p-6 space-y-6">
            <div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">
                    QR Code Automation
                </h1>
                <p className="text-sm leading-6 text-muted-foreground mt-2">
                    Configure the form that users will see when they scan a QR
                    code from the photo booth.
                </p>
            </div>

            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue }) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        {/* Interface Settings */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Form Settings</CardTitle>
                                <CardDescription>
                                    Configure the basic appearance and text of
                                    the form.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div>
                                    <Label htmlFor="interface.galleryId">
                                        Gallery
                                    </Label>
                                    <Field name="interface.galleryId">
                                        {({ field }: any) => (
                                            <Select
                                                value={field.value?.toString()}
                                                onValueChange={(value) =>
                                                    setFieldValue(
                                                        'interface.galleryId',
                                                        +value,
                                                    )
                                                }
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a gallery" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {galleries.map(
                                                        (gallery) => (
                                                            <SelectItem
                                                                key={gallery.id}
                                                                value={gallery.id.toString()}
                                                            >
                                                                {gallery.name}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        )}
                                    </Field>
                                </div>

                                <div>
                                    <Label htmlFor="interface.title">
                                        Form Title (optional)
                                    </Label>
                                    <Field
                                        as={Input}
                                        name="interface.title"
                                        placeholder="Enter a title for the form"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="interface.submitButtonText">
                                        Submit Button Text
                                    </Label>
                                    <Field
                                        as={Input}
                                        name="interface.submitButtonText"
                                        placeholder="Next"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="interface.confirmationText">
                                        Confirmation Message
                                    </Label>
                                    <Field
                                        as={Textarea}
                                        name="interface.confirmationText"
                                        placeholder="Great! Look at the photo booth for next steps!"
                                        rows={3}
                                    />
                                </div>
                            </CardContent>
                        </Card>

                        {/* Data Fields */}
                        <Card>
                            <CardHeader>
                                <CardTitle>Form Fields</CardTitle>
                                <CardDescription>
                                    Add fields to collect information from
                                    users. Use the arrow buttons to reorder
                                    fields.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FieldArray name="dataFields">
                                    {({ push, remove }) => (
                                        <div className="space-y-4">
                                            {values.dataFields.map(
                                                (field, index) => (
                                                    <div
                                                        key={`field-${index}`}
                                                        className="border rounded-lg p-4 space-y-3 bg-background"
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center space-x-2">
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        moveFieldUp(
                                                                            index,
                                                                            setFieldValue,
                                                                            values,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        index ===
                                                                        0
                                                                    }
                                                                    className="text-muted-foreground hover:text-muted-foreground"
                                                                >
                                                                    <ChevronUp className="h-4 w-4" />
                                                                </Button>
                                                                <Button
                                                                    type="button"
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={() =>
                                                                        moveFieldDown(
                                                                            index,
                                                                            setFieldValue,
                                                                            values,
                                                                        )
                                                                    }
                                                                    disabled={
                                                                        index ===
                                                                        values
                                                                            .dataFields
                                                                            .length -
                                                                            1
                                                                    }
                                                                    className="text-muted-foreground hover:text-muted-foreground"
                                                                >
                                                                    <ChevronDown className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() =>
                                                                    remove(
                                                                        index,
                                                                    )
                                                                }
                                                                className="text-red-600 hover:text-red-700"
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </div>

                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                            <div>
                                                                <Label>
                                                                    Field Type
                                                                </Label>
                                                                <Field
                                                                    name={`dataFields.${index}.type`}
                                                                >
                                                                    {({
                                                                        field,
                                                                    }: any) => (
                                                                        <Select
                                                                            value={
                                                                                field.value
                                                                            }
                                                                            onValueChange={(
                                                                                value,
                                                                            ) =>
                                                                                setFieldValue(
                                                                                    `dataFields.${index}.type`,
                                                                                    value,
                                                                                )
                                                                            }
                                                                        >
                                                                            <SelectTrigger>
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent>
                                                                                {FIELD_TYPES.map(
                                                                                    (
                                                                                        type,
                                                                                    ) => (
                                                                                        <SelectItem
                                                                                            key={
                                                                                                type.value
                                                                                            }
                                                                                            value={
                                                                                                type.value
                                                                                            }
                                                                                        >
                                                                                            {
                                                                                                type.label
                                                                                            }
                                                                                        </SelectItem>
                                                                                    ),
                                                                                )}
                                                                            </SelectContent>
                                                                        </Select>
                                                                    )}
                                                                </Field>
                                                            </div>

                                                            <div>
                                                                <Label>
                                                                    Field Name
                                                                </Label>
                                                                <Field
                                                                    as={Input}
                                                                    name={`dataFields.${index}.name`}
                                                                    placeholder="e.g., Full Name"
                                                                />
                                                            </div>

                                                            <div>
                                                                <Label>
                                                                    Placeholder
                                                                    Text
                                                                </Label>
                                                                <Field
                                                                    as={Input}
                                                                    name={`dataFields.${index}.placeholder`}
                                                                    placeholder="e.g., Enter your name"
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Hidden field for galleryId */}
                                                        <Field
                                                            type="hidden"
                                                            name={`dataFields.${index}.galleryId`}
                                                            value={
                                                                values.interface
                                                                    .galleryId
                                                            }
                                                        />
                                                    </div>
                                                ),
                                            )}

                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={() =>
                                                    push({
                                                        type: 'text',
                                                        name: '',
                                                        placeholder: '',
                                                        galleryId:
                                                            values.interface
                                                                .galleryId,
                                                    })
                                                }
                                                className="w-full"
                                            >
                                                <Plus className="size-4" />
                                                Add Field
                                            </Button>
                                        </div>
                                    )}
                                </FieldArray>
                            </CardContent>
                        </Card>

                        {/* Submit Button */}
                        <div className="flex justify-end">
                            <Button type="submit" disabled={isSubmitting}>
                                {isSubmitting ? 'Saving...' : 'Save Settings'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
